/**
 * Item refs (decision 0035, D5): new items read `<project key>-n` (DIC-41). The key is derived from the project's slug
 * (slugs are immutable) and frozen by a pinned test; every minted ref is stored verbatim in ItemCreated and resolved by
 * its exact stored text, so a later key change can never break an old ref. Old items keep T-n, I-n and C-<seq>.
 */
import type { Ids, Item, Project, ProjectState } from "./types"

/**
 * The project key of a slug: its letters and digits, upper-cased, the first 3; `P` in front when that starts with a
 * digit; padded with `I` to 2 characters. `dictly` → `DIC`, `2048-game` → `P204`, `a` → `AI`. FROZEN: changing it
 * needs a decision record (refs already minted stay as stored either way).
 */
export const deriveKey = (slug: string): Ids["ProjectKey"] => {
  let k = slug
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
    .slice(0, 3)
  if (/^[0-9]/.test(k)) k = `P${k}`
  while (k.length < 2) k = `${k}I`
  return k as Ids["ProjectKey"]
}

/** The project's key for new item refs (no stored key this release). */
export const projectKeyOf = (project: Pick<Project, "slug">): Ids["ProjectKey"] => deriveKey(project.slug)

/** The number of the next item ref: never repeats an old T or I number. */
export const nextItemNumber = (s: ProjectState): number =>
  Math.max(s.refCounters.items ?? 0, s.refCounters.T, s.refCounters.I) + 1

/** The next item ref, e.g. DIC-13. */
export const nextItemRef = (s: ProjectState): Ids["ShortRef"] =>
  `${s.project ? projectKeyOf(s.project) : "IT"}-${nextItemNumber(s)}` as Ids["ShortRef"]

/** An item by its exact stored ref, case-insensitive ("dic-41", "T-12", "c-57"). */
export const itemByRef = (s: ProjectState, ref: string): Item | undefined => {
  const r = ref.trim().toUpperCase()
  return s.items.find((t) => t.ref.toUpperCase() === r)
}

/**
 * The hint for a ref that matches no item: "DIC-12 doesn't exist. Items from before the update keep their refs: T-12
 * exists" (only the refs that exist are named), or null when the ref names an item.
 */
export const refMissHint = (s: ProjectState, ref: string): string | null => {
  if (itemByRef(s, ref)) return null
  const m = /^([A-Za-z][A-Za-z0-9]*)-([1-9][0-9]*)$/.exec(ref.trim())
  if (!m) return `${ref} is not an item ref`
  const n = m[2] ?? ""
  const old = ["T", "I"].map((k) => `${k}-${n}`).filter((r) => itemByRef(s, r) !== undefined)
  const head = `${ref.trim().toUpperCase()} doesn't exist.`
  if (old.length === 0) return head
  return `${head} Items from before the update keep their refs: ${old.join(" or ")} ${old.length === 1 ? "exists" : "exist"}.`
}
